import {
  consumerCommunicationApiClient,
  cxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  ConsumerSearchFilters,
  ConsumerSearchResult,
  ConsumerSearchResultWithBill,
  getConsumerBillFilters,
  searchConsumerwithExtraInfoFilters,
} from "./types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const callCenterDashboardApi = {
  searchConsumer: async (
    filters: ConsumerSearchFilters
  ): Promise<{ result: ConsumerSearchResult[] }> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-search/info",
      (query) => {
        query.push("remote_utility_id", REMOTE_UTILITY_ID);
        query.push("search_data", filters.searchData);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerSearchResult[] }>(
      response.data
    );
  },

  searchConsumerwithExtraInfo: async (
    filters: searchConsumerwithExtraInfoFilters
  ): Promise<{ results: ConsumerSearchResultWithBill[] }> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (query) => {
        query.push("remote_utility_id", REMOTE_UTILITY_ID);
        query.push("search_data", filters.searchData);
        query.push("is_extra_info", filters.isExtraInfo);
        query.push("active_consumers", 1);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ results: ConsumerSearchResultWithBill[] }>(
      response.data
    );
  },

  getCallPurpose: async (remoteUtilityId: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerCommunication",
      "choices/call_purpose",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.result ?? [];
  },

  getCallStatus: async (remoteUtilityId: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerCommunication",
      "choices/call_status",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.result ?? [];
  },

  createCallEntry: async (data: any): Promise<any> => {
    const payload = {
      consumer: data.consumer,
      call_purpose: data.call_purpose,
      call_status: data.call_status,
      call_time: data.call_time,
      call_date: data.call_date,
      notes: data.notes,
      remote_utility_id: data.remote_utility_id,
    };

    try {
      const response = await consumerCommunicationApiClient.post(
        "calls/",
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },

  getSummaryDashboard: async (remoteUtilityId: any, createdBy: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerCommunication",
      "consumer-summary-executive-dashboard",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("created_by", createdBy);
        return query;
      }
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.result ?? [];
  },

  getAllCallEntries: async (remoteUtilityId: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerCommunication",
      "calls",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("limit", 5);
        return query;
      }
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.results ?? [];
  },

  getConsumerBill: async (
    filters: getConsumerBillFilters
  ): Promise<{ result: any }> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-bill",
      (query) => {
        query.push("remote_utility_id", REMOTE_UTILITY_ID);
        query.push("consumer_number", filters.consumerNumber);
        query.push("address_required", filters.addressRequired);
        query.push("fetch_latest", filters.fetchLatest);
        query.push("is_plan_required", filters.isPlanRequired);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: any }>(response.data);
  },

  downloadBill: async (billId: string): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      `download-bill/${billId}`
    );
    const response = await cxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },
};
